import { getAuth, signOut } from 'firebase/auth';
import { SnapshotIn, types } from 'mobx-state-tree';

import { MeQuery } from 'generated/graphql';
import { safeLocalStorage } from 'helpers/localStorage';
import { logout, LogoutOptions } from 'server/actions/logout';

export enum LoginStatus {
  LoggedIn = 'LoggedIn',
  LoggedOut = 'LoggedOut',
  Pending = 'Pending',
  Error = 'Error',
}

const LoggedInUser = types.model('LoggedInUser', {
  id: types.identifier,
  name: types.string,
  email: types.maybe(types.string),
  profileImage: types.maybe(types.string),
  phoneNumber: types.maybe(types.string),
  createdAt: types.string,
  isNew: types.boolean,
});

const AuthenticatedUser = types.model('AuthenticatedUser', {
  id: types.identifier,
});

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface ILoggedInUser extends SnapshotIn<typeof LoggedInUser> {}

function getLoggedInUserFromMeQuery(me: MeQuery['me'], isNew: boolean): ILoggedInUser | undefined {
  if (me == null) {
    return undefined;
  }
  const user: ILoggedInUser = {
    id: me.id,
    name: me.name,
    email: me.email ?? undefined,
    profileImage: me.profileImage ?? undefined,
    createdAt: me.createdAt,
    isNew,
  };
  return user;
}

export const UserStore = types
  .model('UserStore', {
    status: types.optional(
      types.enumeration('LoginStatus', [
        LoginStatus.LoggedIn,
        LoginStatus.LoggedOut,
        LoginStatus.Pending,
        LoginStatus.Error,
      ]),
      LoginStatus.LoggedOut,
    ),
    user: types.maybe(LoggedInUser),
    authenticatedUser: types.maybe(AuthenticatedUser),
  })
  .actions((self) => ({
    loggedIn(user: MeQuery['me'], isNew: boolean) {
      const u = getLoggedInUserFromMeQuery(user, isNew);
      self.user = LoggedInUser.create(u);
      if (user?.authenticatedUser != null) {
        const au = user.authenticatedUser ?? undefined;
        self.authenticatedUser = AuthenticatedUser.create(au);
      }
      self.status = LoginStatus.LoggedIn;
    },
    logOut(opts?: LogoutOptions) {
      self.status = LoginStatus.LoggedOut;
      self.user = undefined;
      Promise.all([signOut(getAuth()), logout(opts), clearLocalStorage()]);
    },
    loginError() {
      self.status = LoginStatus.Error;
    },
  }));

const clearLocalStorage = () => {
  safeLocalStorage.clear();
};

export const createUserStore = () => {
  return UserStore.create({});
};
